// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import {fetchEffectDataV2} from 'Utils/react-utils';
import {EXISTING_WORKBOARD_TEMPLATE, NEW_WORKBOARD_TEMPLATE} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {DUPLICATE_ENTITY_TYPES, normalizeName} from '../../utils/workboard-conflict-utils';
import {
    createNamesToIdsMap,
    getWorkboardDataForNameValidation,
    getWorkboardDateTimeKey,
    isDuplicateNameForEntity,
    removeEntityIdFromKeyToIdsMap
} from '../../utils/workboard-data-utils';

function useWorkboardDuplicateValidation({
    detailsDate,
    detailsName,
    handleNameValidation,
    isEditing,
    membershipId,
    nameValidation = {},
    type,
    workboardId,
    translations
}) {
    const isTemplate = type === EXISTING_WORKBOARD_TEMPLATE || type === NEW_WORKBOARD_TEMPLATE;

    const [existingValidationIdsMap, setExistingValidationIdsMap] = React.useState(new Map());

    const duplicateEntityType = isTemplate ? DUPLICATE_ENTITY_TYPES.TEMPLATE : DUPLICATE_ENTITY_TYPES.WORKBOARD;
    const isDuplicateAttempted = Boolean(nameValidation.isTouched && nameValidation.duplicateEntityType === duplicateEntityType);
    const normalizedName = React.useMemo(() => {
        return isTemplate ? normalizeName(detailsName) : '';
    }, [detailsName, isTemplate]);
    const normalizedDateTime = React.useMemo(() => getWorkboardDateTimeKey(detailsDate), [detailsDate]);

    const validationIdsMap = React.useMemo(() => {
        if (!isTemplate) {
            return new Map();
        }

        if (!isEditing || !workboardId) {
            return existingValidationIdsMap;
        }

        return removeEntityIdFromKeyToIdsMap(existingValidationIdsMap, workboardId);
    }, [existingValidationIdsMap, isEditing, isTemplate, workboardId]);

    const duplicateValidation = React.useMemo(() => {
        if (isTemplate) {
            const hasLocalDuplicate = isDuplicateNameForEntity({
                normalizedName,
                existingNameIdsMap: validationIdsMap,
                isEditing: false,
                currentEntityId: null
            });
            const forcedMatch = Boolean(
                nameValidation.forcedDuplicateName &&
                nameValidation.forcedDuplicateName === normalizedName
            );
            const isDuplicateName = hasLocalDuplicate || forcedMatch;

            return {
                forcedDuplicateName: forcedMatch ? nameValidation.forcedDuplicateName : null,
                forcedDuplicateDateTime: null,
                isDuplicateName,
                showDuplicateNameError: isDuplicateName,
                duplicateNameMessage: translations.ONLINK_TEMPLATE_DUPLICATED_NAME_MESSAGE,
                showDuplicateStartTimeError: false,
                duplicateStartTimeMessage: null
            };
        }

        const forcedMatch = Boolean(
            nameValidation.forcedDuplicateDateTime &&
            nameValidation.forcedDuplicateDateTime === normalizedDateTime
        );
        const isDuplicateName = isDuplicateAttempted && forcedMatch;

        return {
            forcedDuplicateName: null,
            forcedDuplicateDateTime: forcedMatch ? nameValidation.forcedDuplicateDateTime : null,
            isDuplicateName,
            showDuplicateNameError: false,
            duplicateNameMessage: null,
            showDuplicateStartTimeError: isDuplicateName,
            duplicateStartTimeMessage: translations.ONLINK_WORKBOARD_DUPLICATED_DATE_TIME_MESSAGE
        };
    }, [isDuplicateAttempted, isTemplate, nameValidation.forcedDuplicateDateTime, nameValidation.forcedDuplicateName, normalizedDateTime, normalizedName, validationIdsMap]);

    React.useEffect(() => {
        handleNameValidation({
            isDuplicate: duplicateValidation.isDuplicateName,
            duplicateEntityType,
            forcedDuplicateName: duplicateValidation.forcedDuplicateName,
            forcedDuplicateDateTime: duplicateValidation.forcedDuplicateDateTime
        });
    }, [duplicateEntityType, duplicateValidation.forcedDuplicateDateTime, duplicateValidation.forcedDuplicateName, duplicateValidation.isDuplicateName, handleNameValidation]);

    React.useEffect(() => {
        if (!membershipId) {
            setExistingValidationIdsMap(new Map());

            return undefined;
        }

        if (!isTemplate) {
            return undefined;
        }

        return fetchEffectDataV2(async ({signal}) => {
            const {
                namesData,
                idKey
            } = await getWorkboardDataForNameValidation({
                isTemplate,
                membershipId,
                signal
            });

            setExistingValidationIdsMap(createNamesToIdsMap(namesData, idKey));
        });
    }, [isTemplate, membershipId]);

    return {
        isTemplate,
        duplicateEntityType,
        duplicateValidation
    };
}

export default useWorkboardDuplicateValidation;
